import type { RecoveryAnchor } from '@/types'
import type { IRecoveryAnchorRepository } from './recoveryAnchorTypes'

const STORAGE_KEY = 'debtos:recoveryAnchor:v1'

/**
 * Single-object localStorage backing for the recovery anchor. There's exactly
 * one anchor per workspace today (single-workspace local), so we serialize the
 * whole object into one key — no list semantics needed.
 *
 * Read failures (corrupt JSON, etc.) return null rather than throwing so the
 * UI can fall through to its auto-init code path.
 */
export class LocalStorageRecoveryAnchorRepo implements IRecoveryAnchorRepository {
  async get(): Promise<RecoveryAnchor | null> {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (!raw) return null
      const parsed = JSON.parse(raw) as RecoveryAnchor
      // Defensive shape check — anchor with no startDate or startingTotal is
      // useless and should be treated as "unset" so auto-init runs.
      if (typeof parsed.startDate !== 'string' || typeof parsed.startingTotal !== 'number') {
        return null
      }
      return parsed
    } catch {
      return null
    }
  }

  async set(anchor: RecoveryAnchor): Promise<void> {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(anchor))
    } catch (err) {
      // QuotaExceededError / Safari-private-mode failures surface to the
      // caller so the UI can react instead of silently keeping in-memory
      // state ahead of disk.
      throw new Error(
        `Failed to save to local storage (${STORAGE_KEY}): ` +
          (err instanceof Error ? err.message : String(err))
      )
    }
  }

  async clear(): Promise<void> {
    try {
      localStorage.removeItem(STORAGE_KEY)
    } catch (err) {
      throw new Error(
        `Failed to clear local storage (${STORAGE_KEY}): ` +
          (err instanceof Error ? err.message : String(err))
      )
    }
  }
}
